import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Divider,
  useTheme,
  useMediaQuery,
  Avatar,
  AppBar,
  Toolbar,
  IconButton,
  Stack,
  Chip,
  Dialog,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  DirectionsCar as CarIcon,
  ArrowBack as ArrowBackIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
  CalendarMonth as CalendarIcon,
  Speed as SpeedIcon,
  LocalOffer as LocalOfferIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as ResetIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { publicVehicleService } from '../../services/publicApi';
import { trackShowcaseEvent } from '../../services/showcaseAnalytics';
import { Vehicle } from '../../types';
import { STORE_BRANDING, storeWhatsappLink } from '../../config/storeBranding';

const BRAND_RED = STORE_BRANDING.brandRed || '#DD2F30';
const API_BASE = (process.env.REACT_APP_API_URL || 'https://api.carmanager.com.br').replace(/\/$/, '');

const getImageUrl = (url: string) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE}${url.startsWith('/') ? url : `/${url}`}`;
};

const fontHeading = '"Outfit", sans-serif';
const fontBody = '"DM Sans", sans-serif';

const SWIPE_THRESHOLD_PX = 45;

const sectionHeading = (title: string) => (
  <Typography
    sx={{
      fontFamily: fontHeading,
      fontWeight: 700,
      fontSize: { xs: 17, sm: 18 },
      color: '#121212',
      mb: 2,
      letterSpacing: '-0.02em',
    }}
  >
    {title}
  </Typography>
);

const VitrineDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [pinch, setPinch] = useState<{
    active: boolean;
    startDistance: number;
    startZoom: number;
    startPan: { x: number; y: number };
    startCenter: { x: number; y: number };
  }>({ active: false, startDistance: 0, startZoom: 1, startPan: { x: 0, y: 0 }, startCenter: { x: 0, y: 0 } });
  const [pointers, setPointers] = useState<Record<number, { x: number; y: number }>>({});
  const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);
  const [shareSnackbarMessage, setShareSnackbarMessage] = useState('');

  const heroSwipeStartX = useRef<number | null>(null);
  const heroSwipeBlockTap = useRef(false);
  const viewerSwipeTouch = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Veículo não encontrado.');
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await publicVehicleService.getVehicleById(id);
        setVehicle(data);
        if (data) trackShowcaseEvent('vehicle_view', data.id);
        if (!data) setError('Veículo não encontrado.');
      } catch {
        setError('Erro ao carregar o veículo.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const formatCurrency = (value: number) =>
    value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) ?? '';

  const getSalePrice = (v: Vehicle) => {
    const sale = v.sale_price;
    if (sale != null && Number(sale) > 0) return Number(sale);
    return v.purchase_price || 0;
  };

  const getSalePricePromo = (v: Vehicle) => {
    const promo = v.sale_price_promo;
    if (promo != null && Number(promo) > 0) return Number(promo);
    return null;
  };

  const getWhatsappLink = (v: Vehicle) => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const msg = `Olá, tenho interesse no carro ${v.brand} ${v.model} ${v.year}. Podemos conversar? ${url ? `\\n\\nLink: ${url}` : ''}`.trim();
    return `https://wa.me/${STORE_BRANDING.whatsappE164}?text=${encodeURIComponent(msg)}`;
  };

  const images = vehicle?.images?.length
    ? vehicle.images.map((img) => getImageUrl(img.url)).filter(Boolean)
    : [];

  const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
  const resetViewerTransform = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleShareVehicle = useCallback(async () => {
    if (!vehicle) return;
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (!url) return;
    const title = `${vehicle.brand} ${vehicle.model}`;
    const text = `Confira: ${vehicle.brand} ${vehicle.model} ${vehicle.year}`;
    trackShowcaseEvent('share', vehicle.id);

    try {
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        await navigator.share({ title, text, url });
        return;
      }
    } catch (e: unknown) {
      const name = e && typeof e === 'object' && 'name' in e ? String((e as { name?: string }).name) : '';
      if (name === 'AbortError') return;
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setShareSnackbarMessage('Link copiado. É só colar e enviar.');
        setShareSnackbarOpen(true);
        return;
      }
    } catch {
      /* fallback abaixo */
    }

    try {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setShareSnackbarMessage('Link copiado. É só colar e enviar.');
      setShareSnackbarOpen(true);
    } catch {
      setShareSnackbarMessage('Não foi possível copiar. Copie o endereço na barra do navegador.');
      setShareSnackbarOpen(true);
    }
  }, [vehicle]);

  const openViewerAt = (idx: number) => {
    if (!images.length) return;
    const safe = clamp(idx, 0, images.length - 1);
    setViewerIndex(safe);
    resetViewerTransform();
    setPointers({});
    setPinch({ active: false, startDistance: 0, startZoom: 1, startPan: { x: 0, y: 0 }, startCenter: { x: 0, y: 0 } });
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setIsPanning(false);
    setPointers({});
    setPinch({ active: false, startDistance: 0, startZoom: 1, startPan: { x: 0, y: 0 }, startCenter: { x: 0, y: 0 } });
    viewerSwipeTouch.current = null;
  };

  const goPrev = () => {
    if (images.length <= 1) return;
    setViewerIndex((i) => {
      const next = (i - 1 + images.length) % images.length;
      return next;
    });
    resetViewerTransform();
  };

  const goNext = () => {
    if (images.length <= 1) return;
    setViewerIndex((i) => {
      const next = (i + 1) % images.length;
      return next;
    });
    resetViewerTransform();
  };

  const goPrevInline = () => {
    if (images.length <= 1) return;
    setSelectedImageIndex((i) => (i - 1 + images.length) % images.length);
  };

  const goNextInline = () => {
    if (images.length <= 1) return;
    setSelectedImageIndex((i) => (i + 1) % images.length);
  };

  const canHeroSwipe = isMobile && images.length > 1;

  const onHeroTouchStart = (e: React.TouchEvent) => {
    if (!canHeroSwipe) return;
    heroSwipeStartX.current = e.touches[0].clientX;
    heroSwipeBlockTap.current = false;
  };

  const onHeroTouchEnd = (e: React.TouchEvent) => {
    if (!canHeroSwipe || heroSwipeStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - heroSwipeStartX.current;
    heroSwipeStartX.current = null;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    heroSwipeBlockTap.current = true;
    if (dx < 0) goNextInline();
    else goPrevInline();
  };

  const openViewerFromHero = () => {
    if (heroSwipeBlockTap.current) {
      heroSwipeBlockTap.current = false;
      return;
    }
    openViewerAt(selectedImageIndex);
  };

  const onFullscreenTouchStart = (e: React.TouchEvent) => {
    if (zoom !== 1 || images.length < 2) {
      viewerSwipeTouch.current = null;
      return;
    }
    if (e.touches.length > 1) {
      viewerSwipeTouch.current = null;
      return;
    }
    viewerSwipeTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const onFullscreenTouchEnd = (e: React.TouchEvent) => {
    if (zoom !== 1 || images.length < 2 || viewerSwipeTouch.current == null) return;
    const start = viewerSwipeTouch.current;
    if (e.touches.length > 0) return;
    viewerSwipeTouch.current = null;
    const cx = e.changedTouches[0].clientX;
    const cy = e.changedTouches[0].clientY;
    const dx = cx - start.x;
    const dy = cy - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) goNext();
    else goPrev();
  };

  // Aplicar gesto de pinch-to-zoom e pan (mobile/desktop via Pointer Events)
  useEffect(() => {
    const ids = Object.keys(pointers).map((k) => Number(k));
    if (!viewerOpen) return;

    if (ids.length >= 2) {
      const a = pointers[ids[0]];
      const b = pointers[ids[1]];
      if (!a || !b) return;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.hypot(dx, dy);
      const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };

      setIsPanning(false);
      setPinch((p) => {
        if (!p.active) {
          return {
            active: true,
            startDistance: dist || 1,
            startZoom: zoom,
            startPan: { ...pan },
            startCenter: center,
          };
        }
        const ratio = dist / (p.startDistance || 1);
        const nextZoom = clamp(Number((p.startZoom * ratio).toFixed(2)), 1, 4);
        // manter o centro “estável” ao pinçar: ajustar pan pelo delta do centro
        const centerDx = center.x - p.startCenter.x;
        const centerDy = center.y - p.startCenter.y;
        setZoom(nextZoom);
        if (nextZoom === 1) {
          setPan({ x: 0, y: 0 });
        } else {
          setPan({ x: p.startPan.x + centerDx, y: p.startPan.y + centerDy });
        }
        return p;
      });
      return;
    }

    // Um dedo / mouse: pan quando zoom > 1
    if (ids.length === 1) {
      const pt = pointers[ids[0]];
      if (!pt) return;
      if (zoom <= 1) {
        setIsPanning(false);
        return;
      }
      if (pinch.active) return;
      setIsPanning(true);
      setPan((prev) => {
        // panStart guarda offset (client - pan)
        // se panStart ainda está zerado, inicializa
        if (panStart.x === 0 && panStart.y === 0) {
          setPanStart({ x: pt.x - prev.x, y: pt.y - prev.y });
          return prev;
        }
        return { x: pt.x - panStart.x, y: pt.y - panStart.y };
      });
      return;
    }

    // Nenhum pointer ativo
    setIsPanning(false);
    setPanStart({ x: 0, y: 0 });
  }, [pointers, viewerOpen, zoom, pan, pinch.active, panStart.x, panStart.y]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '70vh',
          bgcolor: '#F5F5F5',
        }}
      >
        <CircularProgress sx={{ color: BRAND_RED }} size={44} thickness={4} />
      </Box>
    );
  }

  if (error || !vehicle) {
    return (
      <Box sx={{ bgcolor: '#F5F5F5', minHeight: '100vh', py: 6 }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
            }}
          >
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontFamily: fontBody }}>
              {error || 'Veículo não encontrado.'}
            </Alert>
            <Button
              component={Link}
              to="/vitrine"
              variant="contained"
              disableElevation
              startIcon={<ArrowBackIcon />}
              sx={{
                bgcolor: BRAND_RED,
                fontFamily: fontHeading,
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                '&:hover': { bgcolor: '#c22829' },
              }}
            >
              Voltar para a vitrine
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  const mainImageUrl = images[selectedImageIndex] || images[0];
  const hasPromo = getSalePricePromo(vehicle) != null && getSalePricePromo(vehicle)! < getSalePrice(vehicle);
  const idLabel = vehicle.no_plate ? 'Sem placa' : vehicle.plate_end ? `Placa final ${vehicle.plate_end}` : null;
  const viewerImageUrl = images[viewerIndex] || mainImageUrl;

  const breadcrumbLinkSx = {
    fontFamily: fontBody,
    fontSize: 14,
    color: 'rgba(18,18,18,0.55)',
    textDecoration: 'none',
    '&:hover': { color: BRAND_RED },
  };

  return (
    <Box sx={{ bgcolor: '#F5F5F5', minHeight: '100vh', color: '#121212' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: '#fff',
          color: '#121212',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <Toolbar sx={{ maxWidth: 1280, mx: 'auto', width: '100%', justifyContent: 'space-between', py: 1.5, px: 2 }}>
          <Link to="/vitrine" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}>
            <img
              src={`${process.env.PUBLIC_URL || ''}/assets/logos/MARCA - R MOTORS horizontal.png`}
              alt="R Motors"
              style={{ height: 36, width: 'auto' }}
            />
          </Link>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
            <Tooltip title="Compartilhar este veículo">
              <IconButton
                onClick={() => void handleShareVehicle()}
                aria-label="Compartilhar link do veículo"
                size="medium"
                sx={{ color: BRAND_RED }}
              >
                <ShareIcon />
              </IconButton>
            </Tooltip>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3 }}>
              <Typography component={Link} to="/vitrine" sx={{ fontFamily: fontBody, fontWeight: 500, color: '#121212', textDecoration: 'none', fontSize: 15 }}>
                Comprar
              </Typography>
              <Typography component={Link} to="/vitrine#veiculos" sx={{ fontFamily: fontBody, fontWeight: 500, color: '#121212', textDecoration: 'none', fontSize: 15 }}>
                Veículos
              </Typography>
              <Button
                component="a"
                href={storeWhatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackShowcaseEvent('whatsapp_click')}
                variant="contained"
                size="medium"
                sx={{
                  fontFamily: fontHeading,
                  fontWeight: 600,
                  fontSize: 14,
                  bgcolor: BRAND_RED,
                  color: '#fff',
                  px: 2,
                  py: 1,
                  '&:hover': { bgcolor: '#c22829' },
                }}
              >
                Fale conosco
              </Button>
            </Box>
            <IconButton
            sx={{ display: { md: 'none' } }}
            component="a"
            href={storeWhatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackShowcaseEvent('whatsapp_click')}
            size="small"
          >
              <Typography sx={{ fontFamily: fontBody, fontSize: 14, color: BRAND_RED }}>Fale conosco</Typography>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 }, px: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Button
            component={Link}
            to="/vitrine#veiculos"
            startIcon={<ArrowBackIcon sx={{ fontSize: 20 }} />}
            sx={{
              fontFamily: fontBody,
              fontWeight: 600,
              color: 'rgba(18,18,18,0.75)',
              textTransform: 'none',
              alignSelf: { xs: 'flex-start', sm: 'center' },
              '&:hover': { bgcolor: 'rgba(221,47,48,0.06)', color: BRAND_RED },
            }}
          >
            Voltar ao estoque
          </Button>
          <Button
            type="button"
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={() => void handleShareVehicle()}
            sx={{
              fontFamily: fontHeading,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              borderColor: 'rgba(0,0,0,0.14)',
              color: 'rgba(18,18,18,0.85)',
              py: 1,
              '&:hover': {
                borderColor: BRAND_RED,
                color: BRAND_RED,
                bgcolor: 'rgba(221,47,48,0.04)',
              },
            }}
          >
            Compartilhar
          </Button>
        </Stack>

        <Breadcrumbs
          separator={<NavigateNextIcon sx={{ fontSize: 18, color: 'rgba(0,0,0,0.25)' }} />}
          sx={{ mb: 3 }}
        >
          <Typography component={Link} to="/vitrine" sx={breadcrumbLinkSx}>
            Início
          </Typography>
          <Typography component={Link} to="/vitrine#veiculos" sx={breadcrumbLinkSx}>
            Veículos
          </Typography>
          <Typography sx={{ fontFamily: fontBody, fontSize: 14, fontWeight: 600, color: '#121212', maxWidth: { xs: 200, sm: 'none' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {vehicle.brand} {vehicle.model}
          </Typography>
        </Breadcrumbs>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                overflow: 'hidden',
                borderRadius: 3,
                border: '1px solid rgba(0,0,0,0.06)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                mb: 3,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  bgcolor: '#eceff1',
                  aspectRatio: '16/10',
                  maxHeight: { md: 520 },
                  touchAction: canHeroSwipe ? 'pan-y' : undefined,
                }}
                onTouchStart={onHeroTouchStart}
                onTouchEnd={onHeroTouchEnd}
              >
                {mainImageUrl ? (
                  <Box
                    component="img"
                    src={mainImageUrl}
                    alt={`${vehicle.brand} ${vehicle.model} ${vehicle.year}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      cursor: 'zoom-in',
                      userSelect: 'none',
                      WebkitUserDrag: 'none',
                    }}
                    draggable={false}
                    onClick={() => openViewerFromHero()}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      minHeight: 240,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(145deg, #eceff1 0%, #dfe3e6 100%)',
                    }}
                  >
                    <CarIcon sx={{ fontSize: 80, color: 'rgba(0,0,0,0.12)' }} />
                  </Box>
                )}
                {images.length > 1 && (
                  <>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        goPrevInline();
                      }}
                      aria-label="Foto anterior"
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: 12,
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(11,15,20,0.55)',
                        color: '#fff',
                        border: '1px solid rgba(255,255,255,0.14)',
                        backdropFilter: 'blur(8px)',
                        '&:hover': { bgcolor: 'rgba(11,15,20,0.72)' },
                      }}
                    >
                      <PrevIcon />
                    </IconButton>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        goNextInline();
                      }}
                      aria-label="Próxima foto"
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 12,
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(11,15,20,0.55)',
                        color: '#fff',
                        border: '1px solid rgba(255,255,255,0.14)',
                        backdropFilter: 'blur(8px)',
                        '&:hover': { bgcolor: 'rgba(11,15,20,0.72)' },
                      }}
                    >
                      <NextIcon />
                    </IconButton>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        px: 1.25,
                        py: 0.6,
                        borderRadius: 999,
                        bgcolor: 'rgba(11,15,20,0.55)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        color: 'rgba(255,255,255,0.9)',
                        fontFamily: fontBody,
                        fontSize: 12,
                        backdropFilter: 'blur(8px)',
                      }}
                    >
                      {selectedImageIndex + 1} / {images.length}
                    </Box>
                  </>
                )}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ position: 'absolute', top: 16, left: 16, flexWrap: 'wrap', gap: 1 }}
                >
                  <Chip
                    icon={<CalendarIcon sx={{ fontSize: '16px !important', color: '#fff !important' }} />}
                    label={vehicle.year}
                    size="small"
                    sx={{
                      fontFamily: fontHeading,
                      fontWeight: 700,
                      fontSize: 12,
                      bgcolor: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                      backdropFilter: 'blur(6px)',
                    }}
                  />
                </Stack>
                {hasPromo && (
                  <Chip
                    icon={<LocalOfferIcon sx={{ fontSize: '16px !important', color: '#fff !important' }} />}
                    label="Oferta"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      fontFamily: fontHeading,
                      fontWeight: 700,
                      fontSize: 12,
                      bgcolor: BRAND_RED,
                      color: '#fff',
                      boxShadow: '0 4px 12px rgba(221,47,48,0.45)',
                    }}
                  />
                )}
              </Box>
              {images.length > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    p: 2,
                    overflow: 'auto',
                    borderTop: '1px solid rgba(0,0,0,0.06)',
                    bgcolor: '#fff',
                  }}
                >
                  {images.map((url, idx) => (
                    <Box
                      key={idx}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedImageIndex(idx)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') setSelectedImageIndex(idx);
                      }}
                      onDoubleClick={() => openViewerAt(idx)}
                      sx={{
                        flexShrink: 0,
                        width: 88,
                        height: 66,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: 2,
                        borderColor: selectedImageIndex === idx ? BRAND_RED : 'rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          borderColor: BRAND_RED,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        },
                      }}
                    >
                      <Box component="img" src={url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>

            {isMobile && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.06)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                }}
              >
                <Typography sx={{ fontFamily: fontHeading, fontWeight: 700, fontSize: 22, color: '#121212', mb: 1, lineHeight: 1.3 }}>
                  {vehicle.brand} {vehicle.model}
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75} sx={{ mb: 2 }}>
                  {idLabel && (
                    <Chip label={idLabel} size="small" variant="outlined" sx={{ fontFamily: fontBody, fontSize: 12, borderColor: 'rgba(0,0,0,0.12)' }} />
                  )}
                  {vehicle.km ? (
                    <Chip icon={<SpeedIcon sx={{ fontSize: '14px !important' }} />} label={`${vehicle.km} km`} size="small" variant="outlined" sx={{ fontFamily: fontBody, fontSize: 12, borderColor: 'rgba(0,0,0,0.12)' }} />
                  ) : null}
                </Stack>
                <Typography variant="overline" sx={{ fontFamily: fontBody, fontSize: 10, letterSpacing: '0.12em', color: 'rgba(18,18,18,0.45)', display: 'block', mb: 0.5 }}>
                  {hasPromo ? 'Preço promocional' : 'Valor'}
                </Typography>
                {hasPromo ? (
                  <Box>
                    <Typography sx={{ fontFamily: fontBody, fontSize: 14, color: 'rgba(18,18,18,0.45)', textDecoration: 'line-through', mb: 0.25 }}>
                      {formatCurrency(getSalePrice(vehicle))}
                    </Typography>
                    <Typography sx={{ fontFamily: fontHeading, fontWeight: 800, fontSize: 26, color: BRAND_RED, letterSpacing: '-0.02em' }}>
                      {formatCurrency(getSalePricePromo(vehicle)!)}
                    </Typography>
                  </Box>
                ) : (
                  <Typography sx={{ fontFamily: fontHeading, fontWeight: 800, fontSize: 26, color: BRAND_RED, letterSpacing: '-0.02em' }}>
                    {formatCurrency(getSalePrice(vehicle))}
                  </Typography>
                )}
              </Paper>
            )}

            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 3,
                border: '1px solid rgba(0,0,0,0.06)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
              }}
            >
              {sectionHeading('Sobre o veículo')}
              {vehicle.notes ? (
                <Typography sx={{ fontFamily: fontBody, fontSize: 15, lineHeight: 1.7, color: 'rgba(18,18,18,0.85)', whiteSpace: 'pre-wrap' }}>
                  {vehicle.notes}
                </Typography>
              ) : (
                <Typography sx={{ fontFamily: fontBody, fontSize: 15, lineHeight: 1.7, color: 'rgba(18,18,18,0.55)' }}>
                  {vehicle.brand} {vehicle.model} {vehicle.year}
                  {idLabel ? ` · ${idLabel}` : ''}
                  {vehicle.km ? ` · ${vehicle.km} km` : ''}
                  {vehicle.color ? ` · Cor ${vehicle.color}` : ''}
                  {vehicle.cambio ? ` · Câmbio ${vehicle.cambio}` : ''}
                </Typography>
              )}

              <Divider sx={{ my: 3, borderColor: 'rgba(0,0,0,0.06)' }} />

              {sectionHeading('Ficha técnica')}
              <Grid container spacing={1.5}>
                {[
                  { label: 'Marca', value: vehicle.brand },
                  { label: 'Modelo', value: vehicle.model },
                  { label: 'Ano', value: String(vehicle.year) },
                  { label: 'Quilometragem', value: vehicle.km || '—' },
                  { label: 'Cor', value: vehicle.color || '—' },
                  { label: 'Câmbio', value: vehicle.cambio || '—' },
                  { label: 'Placa / ID', value: idLabel || '—' },
                ].map((row) => (
                  <Grid item xs={6} sm={4} key={row.label}>
                    <Box
                      sx={{
                        p: 1.75,
                        borderRadius: 2,
                        bgcolor: 'rgba(0,0,0,0.02)',
                        border: '1px solid rgba(0,0,0,0.05)',
                        height: '100%',
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          fontFamily: fontBody,
                          fontSize: 10,
                          letterSpacing: '0.1em',
                          color: 'rgba(18,18,18,0.45)',
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        {row.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: row.label === 'Placa / ID' ? 'monospace' : fontBody,
                          fontWeight: 600,
                          fontSize: 14,
                          color: '#121212',
                          wordBreak: 'break-word',
                        }}
                      >
                        {row.value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ position: isMobile ? 'static' : 'sticky', top: 24 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.06)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  bgcolor: '#fff',
                }}
              >
                {!isMobile && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 72,
                          height: 72,
                          borderRadius: 2,
                          bgcolor: '#eceff1',
                          border: '1px solid rgba(0,0,0,0.06)',
                        }}
                        src={mainImageUrl || undefined}
                      >
                        {!mainImageUrl && (
                          <CarIcon sx={{ color: 'rgba(0,0,0,0.25)', fontSize: 36 }} />
                        )}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontFamily: fontHeading, fontWeight: 700, fontSize: { sm: 20, md: 22 }, color: '#121212', mb: 1, lineHeight: 1.3 }}>
                          {vehicle.brand} {vehicle.model}
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
                          {idLabel && (
                            <Chip label={idLabel} size="small" variant="outlined" sx={{ fontFamily: fontBody, fontSize: 12, height: 26, borderColor: 'rgba(0,0,0,0.12)' }} />
                          )}
                          {vehicle.km ? (
                            <Chip icon={<SpeedIcon sx={{ fontSize: '14px !important' }} />} label={`${vehicle.km} km`} size="small" variant="outlined" sx={{ fontFamily: fontBody, fontSize: 12, height: 26, borderColor: 'rgba(0,0,0,0.12)' }} />
                          ) : null}
                        </Stack>
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 2.5, borderColor: 'rgba(0,0,0,0.06)' }} />
                  </>
                )}

                <Typography variant="overline" sx={{ fontFamily: fontBody, fontSize: 10, letterSpacing: '0.12em', color: 'rgba(18,18,18,0.45)', display: 'block', mb: 0.5 }}>
                  {hasPromo ? 'Preço promocional' : 'Valor'}
                </Typography>
                {hasPromo ? (
                  <>
                    <Typography sx={{ fontFamily: fontBody, fontSize: 14, color: 'rgba(18,18,18,0.45)', textDecoration: 'line-through', mb: 0.25 }}>
                      {formatCurrency(getSalePrice(vehicle))}
                    </Typography>
                    <Typography sx={{ fontFamily: fontHeading, fontWeight: 800, fontSize: { xs: 28, sm: 32 }, color: BRAND_RED, letterSpacing: '-0.02em', mb: 3, lineHeight: 1.15 }}>
                      {formatCurrency(getSalePricePromo(vehicle)!)}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontFamily: fontHeading, fontWeight: 800, fontSize: { xs: 28, sm: 32 }, color: BRAND_RED, letterSpacing: '-0.02em', mb: 3, lineHeight: 1.15 }}>
                    {formatCurrency(getSalePrice(vehicle))}
                  </Typography>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disableElevation
                  startIcon={<ChatIcon />}
                  href={getWhatsappLink(vehicle)}
                  onClick={() => trackShowcaseEvent('whatsapp_click', vehicle.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    py: 1.35,
                    mb: 1.5,
                    borderRadius: 2,
                    fontFamily: fontHeading,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: 15,
                    bgcolor: '#25D366',
                    '&:hover': { bgcolor: '#20BD5A' },
                  }}
                >
                  Falar no WhatsApp
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  startIcon={<PhoneIcon />}
                  sx={{
                    py: 1.35,
                    borderRadius: 2,
                    fontFamily: fontHeading,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: 15,
                    borderColor: 'rgba(0,0,0,0.14)',
                    color: 'rgba(18,18,18,0.85)',
                    '&:hover': {
                      borderColor: BRAND_RED,
                      color: BRAND_RED,
                      bgcolor: 'rgba(221,47,48,0.04)',
                    },
                  }}
                >
                  Ver telefone
                </Button>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Dialog
        open={viewerOpen}
        onClose={closeViewer}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: '#0b0f14',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              px: 1,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              backdropFilter: 'blur(10px)',
              bgcolor: 'rgba(11,15,20,0.65)',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              {images.length > 1 && (
                <>
                  <IconButton onClick={goPrev} sx={{ color: '#fff' }} aria-label="Foto anterior">
                    <PrevIcon />
                  </IconButton>
                  <IconButton onClick={goNext} sx={{ color: '#fff' }} aria-label="Próxima foto">
                    <NextIcon />
                  </IconButton>
                </>
              )}
              <Typography sx={{ fontFamily: fontBody, fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>
                {images.length ? `${viewerIndex + 1} / ${images.length}` : '—'}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                onClick={() => setZoom((z) => clamp(Number((z - 0.25).toFixed(2)), 1, 4))}
                sx={{ color: '#fff' }}
                aria-label="Diminuir zoom"
              >
                <ZoomOutIcon />
              </IconButton>
              <IconButton
                onClick={() => setZoom((z) => clamp(Number((z + 0.25).toFixed(2)), 1, 4))}
                sx={{ color: '#fff' }}
                aria-label="Aumentar zoom"
              >
                <ZoomInIcon />
              </IconButton>
              <IconButton onClick={resetViewerTransform} sx={{ color: '#fff' }} aria-label="Resetar zoom">
                <ResetIcon />
              </IconButton>
              <IconButton
                component="a"
                href={viewerImageUrl}
                target="_blank"
                rel="noreferrer"
                sx={{ color: '#fff' }}
                aria-label="Abrir em nova aba"
              >
                <OpenInNewIcon />
              </IconButton>
              <IconButton
                component="a"
                href={viewerImageUrl}
                download
                sx={{ color: '#fff' }}
                aria-label="Baixar imagem"
              >
                <DownloadIcon />
              </IconButton>
              <IconButton onClick={closeViewer} sx={{ color: '#fff' }} aria-label="Fechar">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          <Box
            sx={{
              position: 'relative',
              flex: 1,
              overflow: 'hidden',
              touchAction: 'none',
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isPanning ? 'grabbing' : zoom > 1 ? 'grab' : 'default',
            }}
            onTouchStart={onFullscreenTouchStart}
            onTouchEnd={onFullscreenTouchEnd}
            onWheel={(e) => {
              e.preventDefault();
              const delta = e.deltaY;
              setZoom((z) => {
                const next = clamp(Number((z + (delta > 0 ? -0.15 : 0.15)).toFixed(2)), 1, 4);
                if (next === 1) setPan({ x: 0, y: 0 });
                return next;
              });
            }}
            onPointerDown={(e) => {
              (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
              setPointers((prev) => ({ ...prev, [e.pointerId]: { x: e.clientX, y: e.clientY } }));
            }}
            onPointerMove={(e) => {
              setPointers((prev) => {
                if (!prev[e.pointerId]) return prev;
                return { ...prev, [e.pointerId]: { x: e.clientX, y: e.clientY } };
              });
            }}
            onPointerUp={(e) => {
              setIsPanning(false);
              setPointers((prev) => {
                const { [e.pointerId]: _, ...rest } = prev;
                return rest;
              });
              setPinch((p) => (p.active ? { ...p, active: false } : p));
            }}
            onPointerCancel={(e) => {
              setIsPanning(false);
              setPointers((prev) => {
                const { [e.pointerId]: _, ...rest } = prev;
                return rest;
              });
              setPinch((p) => (p.active ? { ...p, active: false } : p));
            }}
            onDoubleClick={() => {
              setZoom((z) => {
                const next = z > 1 ? 1 : 2;
                if (next === 1) setPan({ x: 0, y: 0 });
                return next;
              });
            }}
          >
            {viewerImageUrl ? (
              <Box
                component="img"
                src={viewerImageUrl}
                alt={`${vehicle.brand} ${vehicle.model} ${vehicle.year}`}
                draggable={false}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom})`,
                  transformOrigin: 'center',
                  transition: isPanning ? 'none' : 'transform 0.12s ease',
                  willChange: 'transform',
                }}
              />
            ) : (
              <CarIcon sx={{ fontSize: 96, color: 'rgba(255,255,255,0.15)' }} />
            )}
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderTop: '1px solid rgba(255,255,255,0.08)',
              bgcolor: 'rgba(11,15,20,0.65)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography sx={{ fontFamily: fontBody, fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>
              {`Dicas: rolagem = zoom • arraste = mover • duplo clique = zoom rápido${
                images.length > 1 ? ' • com zoom em 100%, deslize na horizontal para trocar a foto' : ''
              }`}
            </Typography>
            <Typography sx={{ fontFamily: fontBody, fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>
              Zoom: {Math.round(zoom * 100)}%
            </Typography>
          </Box>
        </Box>
      </Dialog>

      <Snackbar
        open={shareSnackbarOpen}
        autoHideDuration={4500}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setShareSnackbarOpen(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShareSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{
            fontFamily: fontBody,
            fontWeight: 500,
            borderRadius: 2,
            bgcolor: `${BRAND_RED} !important`,
            color: '#fff',
          }}
        >
          {shareSnackbarMessage}
        </Alert>
      </Snackbar>

      <Box
        component="footer"
        sx={{
          bgcolor: '#1a1a1a',
          color: 'rgba(255,255,255,0.85)',
          mt: 6,
          py: 6,
          px: 2,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <img
                src={`${process.env.PUBLIC_URL || ''}/assets/logos/MARCA - R MOTORS horizontal.png`}
                alt="R Motors"
                style={{ height: 32, width: 'auto', filter: 'brightness(0) invert(1)' }}
              />
              <Typography sx={{ mt: 2, fontFamily: fontBody, color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
                Soluções em veículos com qualidade e transparência.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={{ fontFamily: fontHeading, fontWeight: 600, fontSize: 14, color: '#fff', mb: 1.5 }}>
                Comprar
              </Typography>
              <Typography component={Link} to="/vitrine" sx={{ display: 'block', fontFamily: fontBody, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: 14, mb: 0.5 }}>
                Veículos
              </Typography>
              <Typography component={Link} to="/vitrine#veiculos" sx={{ display: 'block', fontFamily: fontBody, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: 14 }}>
                Estoque
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={{ fontFamily: fontHeading, fontWeight: 600, fontSize: 14, color: '#fff', mb: 1.5 }}>
                Ajuda
              </Typography>
              <Typography component="a" href={storeWhatsappLink} target="_blank" rel="noopener noreferrer" onClick={() => trackShowcaseEvent('whatsapp_click')} sx={{ display: 'block', fontFamily: fontBody, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: 14 }}>
                WhatsApp {STORE_BRANDING.whatsappDisplay}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={{ fontFamily: fontBody, color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
                R Motors © {new Date().getFullYear()}
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', mt: 4, pt: 3 }}>
            <Typography sx={{ fontFamily: fontBody, fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
              Todos os direitos reservados.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default VitrineDetailPage;
